/* eslint-disable @angular-eslint/no-conflicting-lifecycle */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  ErrorHandler,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
  booleanAttribute,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, FormGroupDirective, NgControl, NgForm } from '@angular/forms';
import { ErrorStateMatcher, _ErrorStateTracker } from '@angular/material/core';
import { MatFormFieldControl } from '@angular/material/form-field';
import { convertFileToBase64 } from '@zvoove-market/shared';
// eslint-disable-next-line @typescript-eslint/naming-convention
import { default as FroalaEditor, FroalaOptions } from 'froala-editor';
import { Subject } from 'rxjs';
import { froalaDefaultEditorOptions } from '../../const/froala-default-editor-options';

let nextUniqueId = 0;
export const froalaZvooveColors = [
  // Source https://zvoove.atlassian.net/l/cp/YrSRWGHB
  '#3748DE',
  '#AFB6F2',
  '#212B85',
  '#B2CEFF',
  '#E0EBFF',
  '#3F84FF',
  '#1A1246',
  '#767190',
  '#F39B17',
  '#FAD7A2',
  '#925D0E',
  '#DD3030',
  '#F1ACAC',
  '#851D1D',
  '#29E329',
  '#A9F4A9',
  '#198819',
  // Source https://froala.com/wysiwyg-editor/docs/options/#colorsText
  // Needs to be added as the default colors are overwritten if colorsBackground/colorsText is set
  '#61BD6D',
  '#1ABC9C',
  '#54ACD2',
  '#2C82C9',
  '#9365B8',
  '#475577',
  '#CCCCCC',
  '#41A85F',
  '#00A885',
  '#3D8EB9',
  '#2969B0',
  '#553982',
  '#28324E',
  '#000000',
  '#F7DA64',
  '#FBA026',
  '#EB6B56',
  '#E25041',
  '#A38F84',
  '#EFEFEF',
  '#FFFFFF',
  '#FAC51C',
  '#F37934',
  '#D14841',
  '#B8312F',
  '#7C706B',
  '#D1D5D8',
  'REMOVE',
];

export interface ZvFroalaOptions extends FroalaOptions {
  immediateAngularModelUpdate: false;
}

export function createCustomFroalaOptions<T>(instance: T, createOptions: (instance: T) => Partial<ZvFroalaOptions>) {
  return createOptions(instance);
}

export function createCommonFroalaOptionsFromDefault<T>(instance: T, withImageUpload: boolean, withZvooveColors: boolean) {
  return createCustomFroalaOptions(instance, () => {
    const options = { ...froalaDefaultEditorOptions };
    if (withImageUpload) {
      options.events = {
        // This needs to stay a function, lambda will break because it loses track of `this`
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'image.beforeUpload': function (files: FileList) {
          // eslint-disable-next-line @typescript-eslint/no-this-alias
          const activeEditor = this;
          if (files.length && activeEditor) {
            void convertFileToBase64(files[0]).then((base64) => {
              activeEditor.image.insert(String(base64), false, {}, activeEditor.image.get());
              activeEditor.popups.hideAll();
            });
          }
          // Prevents default workflow of the `image.beforeUpload` event
          return false;
        },
      };
      options.imageInsertButtons = ['imageBack', '|', 'imageUpload'];
      options.toolbarButtons = [
        'paragraphFormat',
        '|',
        'bold',
        'italic',
        'underline',
        'textColor',
        'backgroundColor',
        '|',
        'insertImage',
        'insertLink',
        'quote',
        'insertTable',
        '|',
        'formatUL',
        'formatOL',
        '|',
        'alignLeft',
        'alignCenter',
        'alignRight',
        '|',
        'outdent',
        'indent',
        'insertHR',
        '|',
        'undo',
        'redo',
      ];
    }

    if (withZvooveColors) {
      options.colorsBackground = froalaZvooveColors;
      options.colorsText = froalaZvooveColors;
    }
    return options;
  });
}

@Component({
  selector: 'zv-froala-editor',
  templateUrl: './froala-editor.component.html',
  styleUrls: ['./froala-editor.component.scss'],
  standalone: true,

  host: {
    // Native input properties that are overwritten by Angular inputs need to be synced with
    // the native input element. Otherwise property bindings for those don't work.
    '[attr.id]': 'id',
    '[attr.aria-describedby]': '_ariaDescribedby || null',
    '[attr.aria-disabled]': 'disabled.toString()',
    // Only mark the input as invalid for assistive technology if it has a value since the
    // state usually overlaps with `aria-required` when the input is empty and can be redundant.
    '[attr.aria-invalid]': '(empty && required) ? null : errorState',
    '[attr.aria-required]': 'required.toString()',
  },
  providers: [{ provide: MatFormFieldControl, useExisting: ZvFroalaEditorComponent }],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class ZvFroalaEditorComponent implements ControlValueAccessor, MatFormFieldControl<string>, OnChanges, OnDestroy, OnInit, DoCheck {
  private _errorStateTracker: _ErrorStateTracker;
  ngControl = inject(NgControl, { optional: true, self: true });
  _cd = inject(ChangeDetectorRef);
  _errorHandler = inject(ErrorHandler);

  readonly options = input.required<Partial<ZvFroalaOptions>>();

  /**
   * Implemented as part of MatFormFieldControl.
   *
   * @docs-private
   */
  focused = false;

  /**
   * Implemented as part of MatFormFieldControl.
   *
   * @docs-private
   */
  readonly stateChanges: Subject<void> = new Subject<void>();

  /**
   * Implemented as part of MatFormFieldControl.
   *
   * @docs-private
   */
  controlType = 'zv-froala-editor';

  /**
   * Implemented as part of MatFormFieldControl.
   *
   * @docs-private
   */
  autofilled = false;

  /**
   * Implemented as part of MatFormFieldControl.
   *
   * @docs-private
   */
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input({ transform: booleanAttribute })
  get disabled(): boolean {
    if (this.ngControl && this.ngControl.disabled !== null) {
      return this.ngControl.disabled;
    }
    return this._disabled;
  }
  set disabled(value: boolean) {
    this._disabled = value;
    this._disableEditor(value);
    this.stateChanges.next();
    this._cd.markForCheck();
  }
  protected _disabled = false;

  /**
   * Implemented as part of MatFormFieldControl.
   *
   * @docs-private
   */
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input()
  get id(): string {
    return this._id;
  }
  set id(value: string) {
    this._id = value || this._uid;
  }
  protected _id = '';

  /**
   * Implemented as part of MatFormFieldControl.
   *
   * @docs-private
   */
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() placeholder = '';

  /**
   * Implemented as part of MatFormFieldControl.
   *
   * @docs-private
   */
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input({ transform: booleanAttribute })
  get required(): boolean {
    return this._required;
  }
  set required(value: boolean) {
    this._required = value;
    this._cd.markForCheck();
  }
  protected _required = false;

  /** An object used to control when error messages are shown. */
  get errorStateMatcher() {
    return this._errorStateTracker.matcher;
  }
  set errorStateMatcher(value: ErrorStateMatcher) {
    this._errorStateTracker.matcher = value;
  }

  /** Whether the input is in an error state. */
  get errorState() {
    return this._errorStateTracker.errorState;
  }
  set errorState(value: boolean) {
    this._errorStateTracker.errorState = value;
  }

  /**
   * Implemented as part of MatFormFieldControl.
   *
   * @docs-private
   */
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input()
  get value(): string | null {
    return this._value;
  }
  set value(value: string | null) {
    if (value !== this.value) {
      if (this._editor) {
        this._editor.html.set(value || '');
        this._editor.undo.reset();
        this._editor.undo.saveStep();
      } else {
        this._preInitializationValue = value || '';
      }
      this._value = value;
      this.stateChanges.next();
      this._cd.markForCheck();
    }
  }
  _value: string | null = null;

  @Output() public readonly valueChange = new EventEmitter<string | null>();

  /** Whether the element is readonly. */
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input({ transform: booleanAttribute })
  get readonly(): boolean {
    return this._readonly;
  }
  set readonly(value: boolean) {
    this._readonly = value;
    this._disableEditor(this._readonly);
    this.stateChanges.next();
    this._cd.markForCheck();
  }
  private _readonly = false;

  /**
   * Implemented as part of MatFormFieldControl.
   *
   * @docs-private
   */
  get empty(): boolean {
    return (this._value === null || this._value === undefined) && !this.autofilled;
  }

  /**
   * Implemented as part of MatFormFieldControl.
   *
   * @docs-private
   */
  readonly shouldLabelFloat = true;

  protected _uid = `zv-froala-editor-${nextUniqueId++}`;
  /** The aria-describedby attribute on the input for improved a11y. */
  _ariaDescribedby!: string;

  readonly _editorDivViewChild = viewChild.required('editorDiv', { read: ElementRef<HTMLDivElement> });

  _onModelChange: (val: unknown) => void = () => {
    // not implemented but required by the interface
  };
  _onModelTouched = () => {
    // not implemented but required by the interface
  };

  constructor() {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
    const parentForm = inject(NgForm, { optional: true });
    const parentFormGroup = inject(FormGroupDirective, { optional: true });
    const defaultErrorStateMatcher = inject(ErrorStateMatcher);
    this._errorStateTracker = new _ErrorStateTracker(
      defaultErrorStateMatcher,
      this.ngControl,
      parentFormGroup,
      parentForm,
      this.stateChanges
    );
  }

  ngOnInit() {
    // @ts-expect-error Froala does not provide a .d.ts file for the plugins.
    import('froala-editor/js/plugins.pkgd.min.js')
      .then(() => {
        // Force setter to be called in case id was not specified.
        // eslint-disable-next-line no-self-assign
        this.id = this.id;
        this.createEditor();
      })
      .catch((err) => this._errorHandler.handleError(err));
  }

  private _preInitializationValue = '';
  private _editor: FroalaEditor | null = null;
  private createEditor() {
    if (this._editor) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    const editor = new FroalaEditor(this._editorDivViewChild().nativeElement, {
      ...this.options(),
      events: {
        ...this.options().events,
        initialized: function () {
          if (that.options().events?.initialized) {
            that.options().events?.initialized?.call(this);
          }
          this.html.set(that._preInitializationValue);
        },
        contentChanged: function () {
          that._value = editor.html.get();
          that.valueChange.emit(that.value);
          that._onModelChange(that.value);
        },
        mousedown: function () {
          setTimeout(function () {
            that._onModelTouched();
          }, 0);
        },
        keyup: function () {
          if (that.options().immediateAngularModelUpdate) {
            setTimeout(function () {
              that._value = editor.html.get();
              that.valueChange.emit(that.value);
              that._onModelChange(that.value);
            }, 0);
          }
        },
        focus: function () {
          that.focused = true;
        },
        blur: function () {
          that.focused = false;
        },
      },
    });

    if (this.placeholder) {
      editor.opts.placeholderText = this.placeholder;
    }
    this._editor = editor;
  }

  private _disableEditor(isDisabled: boolean) {
    if (this._editor) {
      if (isDisabled == true) {
        this._editor.edit.off();
        this._editor.toolbar.disable();
      } else {
        this._editor.edit.on();
        this._editor.toolbar.enable();
      }
    }
  }

  ngOnChanges() {
    this.stateChanges.next();
  }

  ngOnDestroy() {
    this._editor?.destroy();
    this._editor = null;
    this.stateChanges.complete();
  }

  ngDoCheck() {
    if (this.ngControl) {
      // We need to re-evaluate this on every change detection cycle, because there are some
      // error triggers that we can't subscribe to (e.g. parent form submissions). This means
      // that whatever logic is in here has to be super lean or we risk destroying the performance.
      this.updateErrorState();
    }
  }

  /** Refreshes the error state of the input. */
  updateErrorState() {
    this._errorStateTracker.updateErrorState();
  }

  /**
   * Implemented as part of MatFormFieldControl.
   *
   * @docs-private
   */
  setDescribedByIds(ids: string[]) {
    this._ariaDescribedby = ids.join(' ');
  }

  /**
   * Implemented as part of MatFormFieldControl.
   *
   * @docs-private
   */
  onContainerClick() {
    if (!this.focused) {
      this.focus();
    }
  }

  /** Focuses the input. */
  focus(): void {
    this._editor?.events.focus();
  }

  writeValue(value: unknown): void {
    this.value = typeof value == 'string' ? value : null;
  }

  registerOnChange(fn: (val: unknown) => void): void {
    this._onModelChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onModelTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
